import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import { allComics, categories, testimonials } from '@/mocks/data';

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const comic = allComics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <SEOSchema type="article" title="漫画未找到 - 韩漫屋" url={`http://fkyy11.com/article/${id}`} />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
            <p className="text-foreground-500 text-lg">该漫画不存在或已被移除。</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
              {t('common:breadcrumbHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const mainCat = categories.find((c) => c.slug === comic.slug);
  const relatedComics = allComics.filter((c) => c.slug === comic.slug && c.id !== comic.id);

  return (
    <>
      <SEOSchema
        type="article"
        title={`${comic.title} - 韩漫屋韩漫在线阅读`}
        description={comic.description.slice(0, 150)}
        url={`http://fkyy11.com/article/${comic.id}`}
        articleData={{
          headline: comic.title,
          author: comic.author,
          datePublished: '2026-06-15',
          dateModified: comic.updateDate,
          image: comic.cover,
        }}
        breadcrumbs={[
          { name: t('common:breadcrumbHome'), url: 'http://fkyy11.com/' },
          { name: mainCat?.name || '分类', url: `http://fkyy11.com/category/${comic.slug}` },
          { name: comic.title, url: `http://fkyy11.com/article/${comic.id}` },
        ]}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: t('common:breadcrumbHome'), href: '/' },
            { label: mainCat?.name || '分类', href: `/category/${comic.slug}` },
            { label: comic.title },
          ]}
        />

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 max-w-5xl mx-auto">
              <div className="lg:w-1/3 flex-shrink-0">
                <div className="relative rounded-lg overflow-hidden aspect-[5/7] max-w-[280px] mx-auto lg:max-w-none">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="lg:w-2/3 flex flex-col justify-start">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    comic.status === '已完结'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-primary-100 text-primary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                    {comic.category}
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight">
                  {comic.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-4 md:gap-6 text-sm text-foreground-600">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-user-line text-foreground-400"></i>
                    <span>{t('common:articleAuthor')}：{comic.author}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-flag-line text-foreground-400"></i>
                    <span>{t('common:articleStatus')}：{comic.status}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-calendar-line text-foreground-400"></i>
                    <span>{t('common:articleUpdateDate')}：{comic.updateDate}</span>
                  </div>
                </div>

                <div className="mt-5 flex items-center gap-2">
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-base ${
                          i < Math.floor(comic.rating)
                            ? 'ri-star-fill text-accent-400'
                            : 'ri-star-line text-foreground-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <span className="text-lg font-bold text-foreground-900">{comic.rating}</span>
                  <span className="text-sm text-foreground-500">· {comic.chapters}{t('common:chapters')}</span>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <button className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-book-read-line"></i>
                    {t('common:startReading')}
                  </button>
                  <button className="inline-flex items-center justify-center gap-2 px-8 py-3 border border-background-200 text-foreground-700 rounded-full font-medium text-sm hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line"></i>
                    {t('common:addBookshelf')}
                  </button>
                </div>

                <div className="mt-8">
                  <h2 className="text-base font-bold text-foreground-900 mb-3">
                    {t('common:articleDescription')}
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="w-full py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="flex items-end justify-between mb-8 md:mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    {t('common:articleRelatedTitle')}
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">喜欢{comic.category}类型的读者也在看</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {relatedComics.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg bg-background-200">
                      <div className="relative w-full aspect-[5/7] overflow-hidden">
                        <img
                          src={rel.cover}
                          alt={rel.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-foreground-950/70 to-transparent"></div>
                        <div className="absolute bottom-3 left-3 right-3">
                          <div className="flex items-center gap-1 text-background-50 text-xs">
                            <i className="ri-star-fill text-accent-400 text-xs"></i>
                            <span>{rel.rating}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 px-1">
                      <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">
                        {rel.author}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="w-full py-14 md:py-20 bg-secondary-100/50">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-secondary-200/60 rounded-full mb-4">
                <i className="ri-chat-3-line text-primary-500 text-sm"></i>
                <span className="text-xs font-medium text-secondary-700">{t('common:articleReviewsTitle')}</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {t('common:articleReviewsTitle')}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto">
              {testimonials.slice(0, 3).map((item) => (
                <div key={item.id} className="bg-background-50 rounded-lg p-5 md:p-6 relative">
                  <i className="ri-double-quotes-l text-3xl text-primary-200 absolute top-4 right-5"></i>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img
                        src={item.avatar}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: item.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-xs"></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {item.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}